import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Image, Loader2, Download, ChevronLeft, ChevronRight, Info } from "lucide-react";
import axios from "axios";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SubscriptionPromptModal } from "@/components/SubscriptionPromptModal";

interface ImageGeneratorProps {
  content: string;
  onImagesGenerated?: (images: string[]) => void;
  onImageSelect: (selectedImages: string[]) => void;
}

export const ImageGenerator = ({ 
  content, 
  onImagesGenerated,
  onImageSelect 
}: ImageGeneratorProps) => {
  const [images, setImages] = useState<string[]>([]);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingImageId, setDownloadingImageId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [usageDetails, setUsageDetails] = useState<{
    current: number;
    limit: number;
    isSubscribed: boolean;
  } | null>(null);
  const imagesPerPage = 2;

  const handleGenerateImages = async () => {
    if (!content.trim()) {
      toast.error("Please write some content first");
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Generating images...");

    try {
      const response = await supabase.functions.invoke('generate-images', {
        body: { content },
        method: 'POST'
      });

      // Check if it's a usage limit response
      if (response.data?.data?.error === 'Usage limit reached') {
        setUsageDetails(response.data.data.details);
        setShowUsageModal(true);
        toast.dismiss(loadingToast);
        return;
      }

      // Handle successful response
      if (response.data?.images) {
        setImages(response.data.images);
        onImagesGenerated?.(response.data.images);
        setSelectedImages([]);
        onImageSelect([]);
        setCurrentPage(0);
        toast.dismiss(loadingToast);
        toast.success("Images generated successfully!");
        return;
      }

      throw new Error('Invalid response format');
    } catch (error) {
      console.error("Error generating images:", error);
      toast.dismiss(loadingToast);
      toast.error("Failed to generate images");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadImage = async (imageUrl: string) => {
    try {
      setDownloadingImageId(imageUrl);
      const response = await axios.get(imageUrl, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `generated-image-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Image downloaded successfully!");
    } catch (error) {
      console.error("Error downloading image:", error);
      toast.error("Failed to download image");
    } finally {
      setDownloadingImageId(null);
    }
  };

  const toggleImageSelection = (imageUrl: string) => {
    const newSelectedImages = selectedImages.includes(imageUrl)
      ? selectedImages.filter(img => img !== imageUrl)
      : [...selectedImages, imageUrl];
    
    setSelectedImages(newSelectedImages);
    onImageSelect(newSelectedImages);
  };

  const totalPages = Math.ceil(images.length / imagesPerPage);
  const startIndex = currentPage * imagesPerPage;
  const displayedImages = images.slice(startIndex, startIndex + imagesPerPage);

  const nextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  return (
    <div className="space-y-4">
      <div data-generate-button className="hidden">
        <Button 
          variant="outline"
          size="sm"
          onClick={handleGenerateImages} 
          disabled={isLoading}
          className="bg-accent hover:bg-accent/80"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Image className="h-4 w-4 mr-2" />
          )}
          {isLoading ? "Generating..." : "Generate Images"}
        </Button>
      </div>
      
      {images.length > 0 && (
        <div className="relative space-y-4">
          <div className="grid grid-cols-2 gap-6 min-h-[300px]">
            {displayedImages.map((imageUrl, index) => (
              <div 
                key={index} 
                className="relative group"
              >
                <div 
                  className={`relative overflow-hidden rounded-lg border cursor-pointer transition-all duration-200 ${
                    selectedImages.includes(imageUrl) 
                      ? 'ring-2 ring-primary ring-offset-2' 
                      : 'hover:ring-1 hover:ring-primary/50'
                  }`}
                  onClick={() => toggleImageSelection(imageUrl)}
                >
                  <AspectRatio ratio={1}>
                    <img
                      src={imageUrl}
                      alt={`Generated image ${startIndex + index + 1}`}
                      className="object-cover w-full h-full"
                    />
                  </AspectRatio>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity
                    bg-primary hover:bg-primary/90 text-primary-foreground
                    border-primary hover:border-primary/90"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadImage(imageUrl);
                  }}
                  disabled={downloadingImageId === imageUrl}
                >
                  {downloadingImageId === imageUrl ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-4">
              <Button
                variant="outline"
                size="icon"
                onClick={prevPage}
                disabled={currentPage === 0}
                className="bg-primary/10 hover:bg-primary/20 border-primary/20"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {currentPage + 1} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={nextPage}
                disabled={currentPage === totalPages - 1}
                className="bg-primary/10 hover:bg-primary/20 border-primary/20"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      <SubscriptionPromptModal
        isOpen={showUsageModal}
        onClose={() => setShowUsageModal(false)}
        onSubscribe={() => window.location.href = '/settings'}
        usage={usageDetails || { current: 0, limit: 0, isSubscribed: false }}
      />
    </div>
  );
};